import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  FormLabel,
  HStack,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Heading, Input, Select, Table } from '~/components';
import 'react-quill/dist/quill.snow.css';
import { TextEditor } from '~/components/TextEditor/TextEditor';
import { SelectItem } from '~/types';
import { queryClient } from '~/utils/react-query';
import { getGroupsAsSelect } from '~/hooks';
import { useZeroCarbonProjectsAsSelect } from '~/hooks/useZeroCarbon/useProjects';
import {
  createZeroCarbonLetter,
  getZeroCarbonLetterById,
  updateZeroCarbonLetter,
} from '~/services/ZeroCarbon';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';

export const ZeroCarbonLettersNew: React.VFC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();
  const [isFetching, setIsFetching] = useState(false);
  const [projeto, setProjeto] = useState('');
  const mensagemInicial =
    '<h3>&lt;NOME_DO_CLIENTE&gt;</h3><p><br></p><h2><strong>Prezado(s) Cliente(s),</strong></h2><p><br></p><p>Queremos expressar nossa sincera gratidão pela sua parceria em se unir, junto a Positivo, nesse propósito para eliminar a pegada de carbono gerada pela utilização dos computadores em sua operação. Agradecemos profundamente sua adesão ao HAAS Zero Carbon e juntos podemos iniciar a jornada da TI Verde.</p><p><br></p><p>Ao reconhecer a importância de reduzir as emissões de carbono provenientes da utilização de computadores, você está demonstrando um compromisso notável com a sustentabilidade.</p><p><br></p><p>Como parceiros, estamos empenhados em apoiar sua empresa na busca por práticas mais sustentáveis no uso de computadores. Além do nosso projeto de eliminar a pegada de carbono, a Positivo dispõe de outras iniciativas em ESG (Governança ambiental, social e corporativa) para contribuir com o meio ambiente de forma responsável.</p><p><br></p><p>Sua parceria é inestimável para construirmos um futuro mais sustentável para todos! Vamos trabalhar lado a lado para alcançar esse objetivo.</p><p><br></p><p>Abaixo você poderá clicar no link para verificar os créditos aposentados em nome de sua empresa. Todos os créditos são validados pela certificadora Verra <a href="https://verra.org/" rel="noopener noreferrer" target="_blank">(clique aqui para conhecer o site)</a>.</p><p><br></p><p>Nome do Projeto: <strong>&lt;NOME_DO_PROJETO&gt;</strong></p><p><br></p><p>Número de série do certificado de aposentadoria dos créditos de carbono: &lt;CODIGO_DO_CERTIFICADO&gt;. Clique <a href="<LINK_DO_CERTIFICADO>" rel="noopener noreferrer" target="_blank">aqui</a> para acessar o certificado.</p><p><br></p><p><br></p><p>Toneladas de Carbono equivalente (tCO2e) compensados neste termo de contratação: <strong>&lt;TOTAL_DE_TONELADAS&gt;.</strong></p><p><br></p><p><br></p><p>Com gratidão,</p><p><br></p><p>Positivo Tecnologia S.A.</p><p><br></p><h5>Conheça mais em: https://www.positivoempresas.com.br/haas-zero-carbon/</h5>';
  const [mensagem, setMensagem] = useState(mensagemInicial);
  const toast = useToast();
  const { formState } = useForm();
  const { errors } = formState;
  const { data: projetos, isFetching: isFetchingProjetos } =
    useZeroCarbonProjectsAsSelect();
  const [parametros, setParametros] = useState<(string | null)[]>([]);
  const [isInvalid, setIsInvalid] = React.useState(false);

  useEffect(() => {
    getGroupsAsSelect(9999).then(_groups => {
      queryClient.setQueryData<SelectItem[]>('groups', _groups);
    });
  }, []);

  useEffect(() => {
    if (id) {
      getZeroCarbonLetterById(Number(id)).then(response => {
        const pjt = projetos?.find(
          p => p.label === response.projeto.toString(),
        );
        setProjeto(pjt?.value || '');
        setMensagem(response.mensagem);
      });
    }
  }, [id, projetos]);

  const checarMensagem = () => {
    const regex = /&lt;[^&]*&gt;/g;
    const result = mensagemInicial.match(regex);
    let parametrosFaltantes = result?.map(r => {
      if (!mensagem.includes(r)) {
        return r;
      }
      return null;
    });
    if (!mensagem.includes('<LINK_DO_CERTIFICADO>')) {
      parametrosFaltantes?.push('LINK_DO_CERTIFICADO');
    }
    parametrosFaltantes = parametrosFaltantes?.filter(it => {
      return it !== null;
    });
    parametrosFaltantes = parametrosFaltantes?.map(p =>
      p!.replace(/&lt;|&gt;/g, ''),
    );
    if (parametrosFaltantes?.length) {
      setParametros(parametrosFaltantes);
      onOpenModal();
    } else {
      salvar(true);
    }
  };

  const validarCampos = () => {
    let isValid = true;
    if (isInvalid) {
      toast({
        title: 'Campo texto é obrigatório',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      isValid = false;
    }
    if (!projeto.trim()) {
      toast({
        title: 'Campo projeto é obrigatório',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      isValid = false;
    }
    return isValid;
  };

  const salvar = (ignorar = false) => {
    if (!validarCampos()) return;
    if (!ignorar) {
      checarMensagem();
      return;
    }
    setIsFetching(true);
    const data = {
      projeto_id: projeto,
      mensagem,
      id: id ? Number(id) : undefined,
    };
    let response;
    if (id) {
      response = updateZeroCarbonLetter(data);
    } else {
      response = createZeroCarbonLetter(data);
    }
    response
      .then(() => {
        toast({
          title: `Carta ${id ? 'atualizada' : 'criada'} com sucesso!`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries(['zero_carbon_letters']);
        voltar();
      })
      .catch((err: any) => {
        toast({
          title: 'Erro ao criar carta',
          description: err.response?.data?.detail || '',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsFetching(false));
  };

  const voltar = () => {
    history.goBack();
  };

  return (
    <Box px="8" py="4">
      <HStack gridGap={3}>
        <Heading>{id ? 'Editar carta' : 'Nova carta'}</Heading>
        {isFetching && <Spinner />}
      </HStack>
      <Stack py={8} w="70%" gridGap={4}>
        <Stack>
          <FormLabel margin={0} fontSize="14px">
            Projeto <span style={{ color: 'red' }}>*</span>
          </FormLabel>
          <Select
            name="projeto"
            placeholder="Selecione"
            onChange={e => {
              setProjeto(e.target.value);
            }}
            error={errors.projeto}
            value={projeto}
            isDisabled={!!id}
          >
            {projetos?.length &&
              projetos.map(p => (
                <option key={p.value} value={p.value} title={p.label}>
                  {p.label.length > 100
                    ? `${p.label.slice(0, 100)}...`
                    : p.label}
                </option>
              ))}
          </Select>
        </Stack>
        <Stack>
          <Alert bg="#f7f2fa" borderRadius="md" color="#9246ce" fontSize="sm">
            <AlertDescription>
              <Text color="black">
                <span style={{ fontWeight: 'bold', color: '#9246ce' }}>
                  Atenção:
                </span>{' '}
                É obrigatório informar no texto da carta os parâmetros a seguir:
                {`<NOME_DO_CLIENTE>; <NOME_DO_PROJETO>; <CODIGO_DO_CERTIFICADO>; <LINK_DO_CERTIFICADO>; <TOTAL_DE_TONELADAS>`}
                .
              </Text>
            </AlertDescription>
          </Alert>
        </Stack>
        <Stack>
          <FormLabel margin={0} fontSize="14px">
            Texto da carta: <span style={{ color: 'red' }}>*</span>
          </FormLabel>
          <TextEditor
            value={mensagem}
            onChange={(value, empty) => {
              setMensagem(value);
              setIsInvalid(empty ?? false);
            }}
          />
        </Stack>

        <HStack justifyContent="space-between" gridGap={3} mt={3}>
          <Button
            variant="outline"
            onClick={() => voltar()}
            isDisabled={isFetching}
          >
            Voltar
          </Button>
          <HStack>
            <Button
              colorScheme="b"
              onClick={() => salvar(false)}
              isLoading={isFetching}
              isDisabled={isFetching}
            >
              Salvar
            </Button>
          </HStack>
        </HStack>
      </Stack>
      <ConfirmationDialog
        confirmar={() => salvar(true)}
        onClose={onCloseModal}
        isOpen={isOpenModal}
        titulo="Salvar carta"
        btnConfirmText="Confirmar"
        isDisabled={isFetching}
      >
        {parametros.length > 1 && (
          <span>
            Os seguintes parâmetros não estão presentes no texto da carta:{' '}
            <b>{parametros.join(', ')}</b>.
          </span>
        )}
        {parametros.length === 1 && (
          <span>
            O seguinte parâmetro não está presente no texto da carta:{' '}
            <b>{parametros}</b>.
          </span>
        )}
        <span>Deseja salvar mesmo assim?</span>
      </ConfirmationDialog>
    </Box>
  );
};
